import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Printer, X, FileText } from "lucide-react";

import MouPrintSheet from "./MouPrintSheets.jsx";
import { formsForCountry } from "./mouPrintForms.js";
import { getMouCase } from "../mouCaseApi.js";
import { describeMouError } from "../mouCaseShared.js";
import { DetailSkeleton } from "../../../components/Skeleton.jsx";
import "./mouPrintForm.css";

const TITLE_ID = "wx-mouprint-title";

/**
 * P164-R5: the printable government forms for one MOU round.
 *
 * Owner: "ต้องพิมพ์ printform ออกมาได้ด้วยครับ จากข้อมูลที่มีครับ ... ผมว่าน่าจะ
 * ต้องพิมพ์จากหน้าจอ [รอบ] ภายในกล่องครับ โดยเอกสารจะเปลี่ยนไปตามประเทศนั้นๆ
 * เพื่อนำไปยื่นให้กับหน่วยงานราชการครับ".
 *
 * HOW THIS PRINTS A PDF, and why it is not a report engine. Crystal Reports was
 * offered and cannot be used: it has no .NET 8 runtime at all - SAP's engine
 * stops at .NET Framework 4.8, it is Windows-only, and it is separately
 * licensed. Every server-side PDF library that would replace it either costs
 * money at this company's size or has to have a Thai font licensed and embedded
 * before it can render a single ก. The browser already has both - correct Thai
 * shaping and a PDF writer - so these sheets are laid out for A4 and printed
 * through it. "บันทึกเป็น PDF / Save as PDF" in the print dialog produces a real
 * PDF file, and the same button prints straight to paper for the counter.
 *
 * WHY THIS ONE OVERLAY IS A PORTAL when no other dialog in this feature is.
 * Everything else here only has to sit on top of the page; this has to REPLACE
 * it on paper. Rendered inside the app shell, the sheets inherit an ancestor
 * that is a fixed-height, overflow-hidden viewport - and a real page.pdf()
 * proved what that costs: the whole three-form pack printed as ONE page with
 * the first form cut off at the top. Portalled to document.body it has no
 * clipping ancestor left, so the print rule can simply hide #root and let the
 * sheets be the page.
 *
 * The forms are loaded from the ROUND's own detail, not from the list row: the
 * list carries the employer and the head count, but แบบ นจ. 2 also needs the
 * origin-country agency and the job type, which only the detail holds. One read
 * on open, and nothing is printed until it lands - a form that went to a
 * government office half-filled because a fetch was still in flight would be a
 * real problem, not a cosmetic one.
 */
export default function MouPrintFormsDialog({ open, plcOrganizationId, mouCaseId, onClose }) {
  const panelRef = useRef(null);
  const [state, setState] = useState({ status: "idle", data: null, error: null });

  const load = useCallback(() => {
    if (!open || !plcOrganizationId || !mouCaseId) {
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading", data: null, error: null });
    getMouCase(plcOrganizationId, mouCaseId)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", data: null, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, plcOrganizationId, mouCaseId]);

  useEffect(() => load(), [load]);

  /**
   * Escape closes this and nothing else. Both the rounds dialog and the company
   * drawer underneath listen for Escape too, so without consuming the event on
   * the capture phase one press would tear down all three and lose the
   * operator's place.
   */
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopImmediatePropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, onClose]);

  const round = state.data;
  const forms = useMemo(() => (round ? formsForCountry(round.countryCode) : []), [round]);

  /**
   * The date printed on the forms' ลงวันที่ lines. Fixed when the round lands
   * rather than read at render time, so a re-render between opening and printing
   * cannot produce two sheets dated differently in the same pack.
   */
  const today = useMemo(() => (round ? new Date() : null), [round]);

  if (!open || !plcOrganizationId || !mouCaseId) {
    return null;
  }

  return createPortal(
    <div
      className="wx-mouprint-root"
      // Tells the drawer this overlay came from that a click in here is not a
      // click outside it - see DetailDrawer's own note. Required because this
      // overlay is portalled out of the drawer's DOM.
      data-wx-overlay=""
      onMouseDown={(event) => {
        if (panelRef.current && !panelRef.current.contains(event.target)) {
          onClose?.();
        }
      }}
    >
      <div className="wx-mouprint-backdrop" aria-hidden="true" />
      <div ref={panelRef} className="wx-mouprint-panel" role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
        {/* Hidden from the printed page: a toolbar on a government form would be
            the first thing an officer asks about. */}
        <header className="wx-mouprint-toolbar">
          <div className="wx-mouprint-toolbar-id">
            <p className="wx-mouprint-kicker">
              <FileText size={15} aria-hidden="true" />
              แบบฟอร์มสำหรับยื่นหน่วยงานราชการ
            </p>
            <h2 id={TITLE_ID} className="wx-mouprint-toolbar-title">
              {round ? `รอบที่ ${round.roundNumber} · ${round.countryLabel}` : "แบบฟอร์มการยื่น MOU"}
            </h2>
            {round && (
              <p className="wx-mouprint-toolbar-sub">
                {forms.length} แบบฟอร์ม · ระบบกรอกข้อมูลที่มีในรอบนี้ให้แล้ว ช่องที่เหลือเว้นว่างไว้ให้กรอกด้วยลายมือ
              </p>
            )}
          </div>

          <div className="wx-mouprint-toolbar-actions">
            <button
              type="button"
              className="wx-button wx-button--primary"
              disabled={state.status !== "ready"}
              onClick={() => window.print()}
            >
              <Printer size={15} aria-hidden="true" />
              พิมพ์ / บันทึกเป็น PDF
            </button>
            <button type="button" className="wx-mouprint-close" onClick={onClose} aria-label="ปิดหน้าต่างแบบฟอร์ม">
              <X size={17} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="wx-mouprint-scroll">
          {state.status === "loading" && <DetailSkeleton label="กำลังเตรียมแบบฟอร์ม..." sections={2} />}

          {state.status === "error" && (
            <div className="wx-mouprint-state" role="alert">
              <p className="wx-mouprint-state-title">ไม่สามารถเตรียมแบบฟอร์มของรอบนี้ได้</p>
              <p className="wx-mouprint-state-body">{describeMouError(state.error, "กรุณาปิดหน้าต่างแล้วลองใหม่อีกครั้ง")}</p>
              <button type="button" className="wx-button wx-button--primary" onClick={load}>
                ลองใหม่อีกครั้ง
              </button>
            </div>
          )}

          {state.status === "ready" && (
            <div className="wx-mouprint-sheets">
              {forms.map((form) => (
                <MouPrintSheet key={form.key} formKey={form.key} round={round} today={today} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
